// NotesTable.jsx - Component for displaying notes table with date filtering
// Shows notes with access key, status, and emission date

import { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "@astryxdesign/core/Card";
import { Stack, HStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { Badge } from "@astryxdesign/core/Badge";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { Selector } from "@astryxdesign/core/Selector";
import { DateRangeInput } from "@astryxdesign/core/DateRangeInput";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Spinner } from "@astryxdesign/core/Spinner";
import { Pagination } from "@astryxdesign/core/Pagination";
import { Table, proportional, pixel } from "@astryxdesign/core/Table";
import { downloadLogsService } from "../../services/downloadLogsService";
import { useAuth } from "../../contexts/AuthContext";

const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10" },
  { value: "25", label: "25" },
  { value: "50", label: "50" },
];

/**
 * NotesTable Component
 * Displays a table of notes with pagination
 *
 * @param {Object} props - Component props
 * @param {number} props.companyId - Company ID
 * @param {string} [props.initialStartDate] - Competência inicial pré-preenchida (YYYY-MM-DD)
 * @param {string} [props.initialEndDate] - Competência final pré-preenchida (YYYY-MM-DD)
 */
const NotesTable = ({ companyId, initialStartDate = "", initialEndDate = "" }) => {
  const { user } = useAuth();
  // State management
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(initialStartDate || "");
  const [endDate, setEndDate] = useState(initialEndDate || "");
  const [noteTypePrestada, setNoteTypePrestada] = useState(false);
  const [noteTypeTomada, setNoteTypeTomada] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    currentPage: 1,
    totalPages: 0,
    hasMore: false,
  });

  // Load notes data
  const loadNotes = useCallback(
    async (customFilters = {}) => {
      if (!companyId) return;

      try {
        setLoading(true);
        setError(null);

        // Determine note_type based on checkboxes
        let noteType = null;
        if (customFilters.note_type !== undefined) {
          noteType = customFilters.note_type;
        } else {
          const prestadaChecked =
            customFilters.note_type_prestada !== undefined
              ? customFilters.note_type_prestada
              : noteTypePrestada;
          const tomadaChecked =
            customFilters.note_type_tomada !== undefined
              ? customFilters.note_type_tomada
              : noteTypeTomada;

          if (prestadaChecked && tomadaChecked) {
            noteType = "both";
          } else if (prestadaChecked) {
            noteType = "prestada";
          } else if (tomadaChecked) {
            noteType = "tomada";
          }
          // If neither is checked, noteType remains null (no filter)
        }

        const notesFilters = {
          limit:
            customFilters.limit !== undefined
              ? customFilters.limit
              : pagination.limit,
          offset:
            customFilters.offset !== undefined
              ? customFilters.offset
              : pagination.offset,
          date_from:
            customFilters.date_from !== undefined
              ? customFilters.date_from
              : startDate || null,
          date_to:
            customFilters.date_to !== undefined
              ? customFilters.date_to
              : endDate || null,
          note_type: noteType,
        };

        const response = await downloadLogsService.getCompanyNotes(
          companyId,
          notesFilters
        );

        if (response.success) {
          const notesData = response.data.notes || [];
          setNotes(notesData);
          setPagination((prev) => response.data.pagination || prev);
        } else {
          setError(response.message || "Erro ao carregar notas");
        }
      } catch (error) {
        console.error("Error loading notes:", error);
        setError("Erro interno ao carregar notas");
      } finally {
        setLoading(false);
      }
    },
    [
      companyId,
      pagination.limit,
      pagination.offset,
      startDate,
      endDate,
      noteTypePrestada,
      noteTypeTomada,
    ]
  );

  // Handle pagination (Pagination do Astryx é 1-based)
  const handlePageChange = (newPage) => {
    if (!hasFiltersApplied) return; // Don't allow pagination without filters

    const newOffset = (newPage - 1) * pagination.limit;
    setPagination((prev) => ({
      ...prev,
      currentPage: newPage,
      offset: newOffset,
    }));

    // Reload notes with new pagination but same filters
    loadNotes({
      date_from: startDate || null,
      date_to: endDate || null,
      note_type_prestada: noteTypePrestada,
      note_type_tomada: noteTypeTomada,
      offset: newOffset,
      limit: pagination.limit,
    });
  };

  // Handle page size change
  const handlePageSizeChange = (newLimit) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(newLimit),
      offset: 0,
      currentPage: 1,
    }));
  };

  // Handle apply filters button
  const handleApplyFilters = () => {
    // Validate that at least one note type is selected
    if (!noteTypePrestada && !noteTypeTomada) {
      setError("Selecione pelo menos um tipo de nota (Prestados ou Tomados)");
      return;
    }

    // Validate dates
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setError("A data inicial não pode ser maior que a data final");
      return;
    }

    // Reset pagination and load notes with filters
    setPagination((prev) => ({
      ...prev,
      offset: 0,
      currentPage: 1,
    }));

    setHasFiltersApplied(true);
    loadNotes({
      date_from: startDate || null,
      date_to: endDate || null,
      note_type_prestada: noteTypePrestada,
      note_type_tomada: noteTypeTomada,
      offset: 0,
      limit: pagination.limit,
    });
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setNoteTypePrestada(false);
    setNoteTypeTomada(false);
    setHasFiltersApplied(false);
    setNotes([]);
    setPagination((prev) => ({
      ...prev,
      total: 0,
      offset: 0,
      currentPage: 1,
      totalPages: 0,
    }));
    setError(null);
  };

  // Handle export to Excel
  const handleExportExcel = async () => {
    if (!companyId) return;

    // Validate that at least one note type is selected
    if (!noteTypePrestada && !noteTypeTomada) {
      setError(
        "Selecione pelo menos um tipo de nota (Prestados ou Tomados) para exportar"
      );
      return;
    }

    // Validate dates
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setError("A data inicial não pode ser maior que a data final");
      return;
    }

    try {
      setExportLoading(true);
      setError(null);

      // Determine note_type for export
      let noteType = null;
      if (noteTypePrestada && noteTypeTomada) {
        noteType = "both";
      } else if (noteTypePrestada) {
        noteType = "prestada";
      } else if (noteTypeTomada) {
        noteType = "tomada";
      }

      const exportFilters = {
        date_from: startDate || null,
        date_to: endDate || null,
        note_type: noteType,
      };

      await downloadLogsService.exportCompanyNotesToExcel(
        companyId,
        exportFilters
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setError("Erro ao exportar para Excel");
    } finally {
      setExportLoading(false);
    }
  };

  // Badge de status da nota
  const statusBadge = (status) => {
    switch (status) {
      case "normal":
        return <Badge variant="success" label="Normal" />;
      case "cancelled":
        return <Badge variant="error" label="Cancelada" />;
      case "substituted":
        return <Badge variant="warning" label="Substituída" />;
      default:
        return <Badge variant="neutral" label="Desconhecido" />;
    }
  };

  // Format CNPJ for display
  const formatCNPJ = (cnpj) => {
    if (!cnpj) return "N/A";
    // Remove formatting if already formatted
    const numbers = cnpj.replace(/\D/g, "");
    if (numbers.length !== 14) return cnpj;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(
      5,
      8
    )}/${numbers.slice(8, 12)}-${numbers.slice(12)}`;
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (!value) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const noteTypeLabel = (note) => {
    const t =
      note.note_type === "prestada" || note.note_type === "prestados"
        ? "prestados"
        : note.note_type === "tomada" || note.note_type === "tomados"
          ? "tomados"
          : "unknown";
    if (t === "prestados") return <Badge variant="info" label="Prestados" />;
    if (t === "tomados") return <Badge variant="purple" label="Tomados" />;
    return <Badge variant="neutral" label="Desconhecido" />;
  };

  // Colunas da tabela de notas
  const columns = [
    {
      key: "access_key",
      header: "Chave de Acesso",
      width: proportional(2),
      renderCell: (n) => {
        const hasError = !n.file_path || n.status !== "normal";
        const key = n.access_key || "N/A";
        const shown =
          key.length > 20 && key !== "N/A" ? `${key.substring(0, 20)}...` : key;
        return (
          <HStack gap={1} vAlign="center">
            <Text color={hasError ? "accent" : "secondary"}>
              <FontAwesomeIcon
                icon={hasError ? "exclamation-triangle" : "file-invoice"}
              />
            </Text>
            <Text size="sm" weight={hasError ? "semibold" : "regular"}>
              {shown}
            </Text>
          </HStack>
        );
      },
    },
    {
      key: "type",
      header: "Tipo",
      width: pixel(120),
      renderCell: (n) => noteTypeLabel(n),
    },
    {
      key: "status",
      header: "Status",
      width: pixel(130),
      renderCell: (n) => statusBadge(n.status),
    },
    {
      key: "competence",
      header: "Data de Competência",
      width: pixel(150),
      renderCell: (n) => (
        <Text size="sm">
          {n.competence_date
            ? downloadLogsService.formatDateForDisplay(n.competence_date)
            : "N/A"}
        </Text>
      ),
    },
    {
      key: "provider",
      header: "Prestador",
      width: proportional(2),
      renderCell: (n) =>
        n.provider_name ? (
          <Stack gap={0}>
            <Text size="sm">{n.provider_name}</Text>
            {n.provider_cnpj && (
              <Text type="supporting">{formatCNPJ(n.provider_cnpj)}</Text>
            )}
          </Stack>
        ) : (
          <Text type="supporting">N/A</Text>
        ),
    },
    {
      key: "taker",
      header: "Tomador",
      width: proportional(2),
      renderCell: (n) =>
        n.taker_name ? (
          <Stack gap={0}>
            <Text size="sm">{n.taker_name}</Text>
            {n.taker_cnpj && (
              <Text type="supporting">{formatCNPJ(n.taker_cnpj)}</Text>
            )}
          </Stack>
        ) : (
          <Text type="supporting">N/A</Text>
        ),
    },
    {
      key: "value",
      header: "Valor Total",
      width: pixel(130),
      renderCell: (n) =>
        n.service_value || n.net_value ? (
          <Text size="sm" weight="medium">
            {formatCurrency(n.service_value || n.net_value)}
          </Text>
        ) : (
          <Text type="supporting">N/A</Text>
        ),
    },
  ];

  const filterSummary =
    hasFiltersApplied &&
    (startDate || endDate || noteTypePrestada || noteTypeTomada)
      ? [
          startDate || endDate
            ? `Competência: ${startDate ? `de ${startDate}` : ""} ${
                endDate ? `até ${endDate}` : ""
              }`.trim()
            : "",
          noteTypePrestada || noteTypeTomada
            ? `Tipo: ${
                noteTypePrestada && noteTypeTomada
                  ? "Prestados e Tomados"
                  : noteTypePrestada
                    ? "Prestados"
                    : "Tomados"
              }`
            : "",
        ]
          .filter(Boolean)
          .join(" | ")
      : "";

  return (
    <Card padding={0}>
      <Stack gap={0}>
        {/* Cabeçalho */}
        <HStack gap={2} vAlign="center" hAlign="between" padding={3}>
          <HStack gap={2} vAlign="center">
            <Text color="accent">
              <FontAwesomeIcon icon="file-alt" />
            </Text>
            <Text weight="semibold">Notas da Empresa</Text>
            <Text type="supporting">Itens por página:</Text>
            <Selector
              label="Itens por página"
              isLabelHidden
              options={PAGE_SIZE_OPTIONS}
              value={String(pagination.limit)}
              onChange={handlePageSizeChange}
              size="sm"
              width={90}
            />
          </HStack>
          {pagination.total > 0 && (
            <Text type="supporting">
              {downloadLogsService.formatNumber(pagination.total)} nota
              {pagination.total > 1 ? "s" : ""}
            </Text>
          )}
        </HStack>

        {/* Filtros */}
        <Stack gap={3} padding={3}>
          <HStack gap={2} vAlign="end" wrap="wrap">
            <DateRangeInput
              label="Competência (início e fim)"
              isOptional
              value={
                startDate && endDate
                  ? { start: startDate, end: endDate }
                  : null
              }
              onChange={(range) => {
                setStartDate(range?.start || "");
                setEndDate(range?.end || "");
              }}
            />
          </HStack>

          <HStack gap={4} vAlign="center" wrap="wrap">
            <Text size="sm" weight="medium">
              Tipo de Nota:
            </Text>
            <CheckboxInput
              label="Prestados"
              value={noteTypePrestada}
              onChange={setNoteTypePrestada}
            />
            <CheckboxInput
              label="Tomados"
              value={noteTypeTomada}
              onChange={setNoteTypeTomada}
            />
          </HStack>

          <HStack gap={2} vAlign="center" wrap="wrap">
            <Button
              label="Filtrar"
              variant="primary"
              icon={<FontAwesomeIcon icon="search" />}
              isDisabled={loading || (!noteTypePrestada && !noteTypeTomada)}
              onClick={handleApplyFilters}
            />
            <Button
              label="Limpar"
              variant="ghost"
              icon={<FontAwesomeIcon icon="times" />}
              isDisabled={loading}
              onClick={handleClearFilters}
            />
            {user?.role === "admin" && (
              <Button
                label={exportLoading ? "Exportando..." : "Exportar Excel"}
                variant="secondary"
                icon={<FontAwesomeIcon icon="file-excel" />}
                isLoading={exportLoading}
                isDisabled={loading || (!noteTypePrestada && !noteTypeTomada)}
                onClick={handleExportExcel}
              />
            )}
          </HStack>

          {filterSummary && (
            <Text type="supporting">Filtros ativos: {filterSummary}</Text>
          )}
        </Stack>

        {/* Conteúdo */}
        <Stack gap={3} padding={3}>
          {loading ? (
            <HStack gap={2} vAlign="center" hAlign="center">
              <Spinner size="sm" label="Carregando notas" />
              <Text type="supporting">Carregando notas...</Text>
            </HStack>
          ) : error ? (
            <Banner status="error" title={error} />
          ) : notes.length > 0 ? (
            <>
              <Table
                data={notes.map((n, i) => ({ ...n, _id: n.id || i }))}
                columns={columns}
                idKey="_id"
                density="compact"
                hasHover
              />
              {pagination.totalPages > 1 && (
                <HStack gap={2} vAlign="center" hAlign="between" wrap="wrap">
                  <Text type="supporting">
                    Página {pagination.currentPage} de {pagination.totalPages}
                  </Text>
                  <Pagination
                    page={pagination.currentPage}
                    onChange={handlePageChange}
                    totalItems={pagination.total ?? 0}
                    pageSize={pagination.limit || 10}
                  />
                </HStack>
              )}
            </>
          ) : (
            <EmptyState
              title={
                hasFiltersApplied
                  ? "Nenhuma Nota Encontrada"
                  : "Selecione os filtros para visualizar as notas"
              }
              description={
                hasFiltersApplied
                  ? "Não há notas registradas para os filtros selecionados."
                  : "Selecione pelo menos um tipo de nota (Prestados ou Tomados) e clique em Filtrar."
              }
              icon={<FontAwesomeIcon icon="file-alt" />}
            />
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export default NotesTable;
